import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Chunked bulk runner + its progress overlay.
 *
 * WHY THIS EXISTS. Booking is paced at ~750ms per courier account (deliberately — Delhivery throttles
 * us above that), so a 500-order Ship is ~6-10 minutes of server work. It used to go out as ONE HTTP
 * request, and the axios client gives up after 30 seconds: the browser threw the response away and
 * showed the generic "Action failed" toast while the backend carried on booking happily. Operators
 * read that as a failure and pressed Ship again — repeatedly — on orders that were mid-booking.
 *
 * So the work is now sent in small chunks. Each chunk answers in seconds, progress is reported after
 * every one, and the summary survives to the end. Throughput is unchanged (the server-side pacing is
 * what sets the speed) — what changes is that the operator can SEE it moving and has no reason to
 * press the button again.
 */

export interface BulkProgress {
  /** Verb shown in the overlay — "Shipping", "Importing", … */
  label: string;
  total: number;
  done: number;
  ok: number;
  failed: number;
}

/**
 * How many items go in one request. Sized so a chunk comfortably beats the client timeout even at the
 * slowest realistic pace: 20 orders × 750ms ≈ 15s of pacing, and the per-request timeout below gives
 * it far more headroom than that.
 */
export const SHIP_CHUNK_SIZE = 20;
/** Import is DB writes rather than courier calls, so chunks can be much larger. */
export const UPLOAD_CHUNK_SIZE = 100;

/**
 * Per-request timeout for a chunk, overriding the client's 30s default. Generous on purpose: a chunk
 * running long is a slow courier, not a dead request, and abandoning it is exactly the behaviour this
 * whole module exists to remove.
 */
export const CHUNK_TIMEOUT_MS = 180_000;

export function useBulkProgress() {
  const [progress, setProgress] = useState<BulkProgress | null>(null);
  const running = progress !== null;

  // Reloading or closing the tab mid-run abandons the remaining chunks — the orders already sent keep
  // booking server-side, but nothing will send the rest. Warn before that happens.
  useEffect(() => {
    if (!running) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  const busyRef = useRef(false);

  /**
   * Push `items` through `call` one chunk at a time, updating the overlay after each.
   *
   * A chunk that THROWS does not abort the run — its items are counted as failed and the next chunk
   * goes out. Losing one chunk to a network blip must not strand the other 480 orders.
   */
  const run = useCallback(async function run<T, R>(opts: {
    label: string;
    items: T[];
    chunkSize: number;
    call: (chunk: T[], startIndex: number) => Promise<R>;
    /** How many of the chunk succeeded / failed, read off that chunk's response. */
    tally: (r: R, chunk: T[]) => { ok: number; failed: number };
    /** Called once per chunk with its result, for accumulating errors etc. */
    onChunk?: (r: R, chunk: T[], startIndex: number) => void;
    /** Called when a chunk request itself fails, so the caller can record the whole chunk as failed. */
    onChunkError?: (err: any, chunk: T[], startIndex: number) => void;
  }): Promise<void> {
    if (busyRef.current) return;          // a run is already going — ignore the second press
    busyRef.current = true;
    const { label, items, chunkSize, call, tally, onChunk, onChunkError } = opts;
    setProgress({ label, total: items.length, done: 0, ok: 0, failed: 0 });
    let ok = 0, failed = 0, done = 0;
    try {
      for (let i = 0; i < items.length; i += chunkSize) {
        const chunk = items.slice(i, i + chunkSize);
        try {
          const r = await call(chunk, i);
          const t = tally(r, chunk);
          ok += t.ok; failed += t.failed;
          onChunk?.(r, chunk, i);
        } catch (err: any) {
          failed += chunk.length;
          onChunkError?.(err, chunk, i);
        }
        done += chunk.length;
        setProgress({ label, total: items.length, done, ok, failed });
      }
    } finally {
      busyRef.current = false;
      setProgress(null);
    }
  }, []);

  return { progress, running, run };
}

export function BulkProgressOverlay({ progress }: { progress: BulkProgress }) {
  const { label, total, done, ok, failed } = progress;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="fixed inset-0 bg-black/50 z-[10060] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl px-7 py-6 flex flex-col items-center gap-3 w-full max-w-sm text-center">
        <Loader2 size={32} className="animate-spin text-primary" />
        <div className="text-sm font-semibold text-slate-800">
          {label} {done} of {total}…
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex gap-4 text-xs">
          <span className="text-emerald-700 font-medium">{ok} done</span>
          {failed > 0 && <span className="text-rose-700 font-medium">{failed} failed</span>}
          <span className="text-slate-500">{pct}%</span>
        </div>
        <div className="text-xs text-slate-500">
          This runs in the background at the courier's pace — please don't close this window or press the
          button again.
        </div>
      </div>
    </div>
  );
}
